import type { paths } from '@connected/api-client/api.generated';
import type { MediaFile } from '@connected/api-client/database/schema';
import type { UseMutationOptions, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { FetchOptions } from 'openapi-fetch';

import { api, client } from '@connected/api-client';
import { useMediaManager } from '@connected/api-client/hooks/use-media-manager';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { DEFAULT_FILE_ORDER, makeAcceptString } from '@connected/ui/config/file';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import type { ElectricalProjectFile, ElectricalProjectFileUploadResult, ElectricalProjectFileUploadValidationRequirements } from '@/hooks/use-electrical-project-files';

import { useElectricalProjectFiles } from '@/hooks/use-electrical-project-files';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { logger } from '@/lib/logger';
import { useOffline } from '@/stores/network-status';

/**
 * ### **`components["schemas"]["ElectricalProjectFileKind"]`**:
 * - DistributionBoard
 * - FinalCircuit
 * - ObjectImage
 * - Observation
 * - TechnicalDrawing
 */
export const SELECTABLE_ENDPOINTS = {
    '/api/v2/electrical/projects/files/upload/distributionboard': 'DistributionBoard',
    '/api/v2/electrical/projects/files/upload/finalcircuit': 'FinalCircuit',
    '/api/v2/electrical/projects/files/upload/objectimage': 'ObjectImage',
    '/api/v2/electrical/projects/files/upload/observation': 'Observation',
    '/api/v2/electrical/projects/files/upload/technicaldrawing': 'TechnicalDrawing',
    '/api/v2/electrical/projects/files/upload/floorplan': 'Floorplan',
    '/api/v2/electrical/projects/files/upload/pvinstallation': 'PVInstallation',
    '/api/v2/electrical/projects/files/upload/pvinverter': 'PVInverter',
    '/api/v2/electrical/projects/files/upload/Measurements': 'Measurements',
    '/api/v2/electrical/projects/files/upload/observationlog': 'ObservationLog',
} as const;

export const SELECTABLE_ENDPOINTS_KEYS = Object.keys(SELECTABLE_ENDPOINTS) as SelectableEndpoint[];

export type SelectableEndpoints = typeof SELECTABLE_ENDPOINTS;
export type SelectableEndpoint = keyof SelectableEndpoints;

export type UseFileUploadOptions<Endpoint extends SelectableEndpoint> = {
    endpoint: Endpoint;
    mutationOptions?: Omit<UseMutationOptions<ElectricalProjectFileUploadResult, Error, FileUploadMutationOptions<Endpoint>, Promise<FileUploadMutationContext>>, 'mutationFn' | 'onMutate' | 'onSuccess' | 'onError'> & {
        onMutate?: (newFile: ElectricalProjectFile, mediaFile: MediaFile) => Promise<void>;
        onSuccess?: (data: ElectricalProjectFileUploadResult, variables: FileUploadMutationOptions<Endpoint>, context: FileUploadMutationContext) => Promise<void>;
        onError?: (error: Error, variables: FileUploadMutationOptions<Endpoint>, context: FileUploadMutationContext) => Promise<void>;
    };
};

export type UseFileUploadResult<Endpoint extends SelectableEndpoint> = {
    mutation: UseMutationResult<ElectricalProjectFileUploadResult, Error, Omit<FileUploadMutationOptions<Endpoint>, 'electricalProjectVersionId'>, Promise<FileUploadMutationContext>>;
    query: UseQueryResult<ElectricalProjectFileUploadValidationRequirements, Error>;
    accept: string | null;
    upload: (options: {
        file: FileUploadMutationOptions<Endpoint>['file'];
        query?: Omit<FileUploadMutationOptions<Endpoint>['query'], 'ElectricalProjectVersionId'>;
    }) => Promise<ElectricalProjectFileUploadResult>;
    isUploading: boolean;
    isLoading: boolean;
    isDisabled: boolean;
};

export type FileUploadMutationContext = {
    previousFiles: ElectricalProjectFile[];
    mediaFile: MediaFile;
};

export type FileUploadMutationOptions<Endpoint extends SelectableEndpoint> = {
    file: File;
    query: FetchOptions<paths[Endpoint]['post']>['params']['query'];
};

export async function handleMutation<Endpoint extends SelectableEndpoint>(endpoint: Endpoint, options: FileUploadMutationOptions<Endpoint>): Promise<ElectricalProjectFileUploadResult> {
    const params = {
        query: {
            ...options.query,
            Order: options.query.Order ?? DEFAULT_FILE_ORDER,
            Description: options.query.Description ?? null,
        },
    };

    const response = await client.POST(endpoint as any, {
        params,
        body: options.file,
        bodySerializer: () => {
            const formData = new FormData();

            formData.append('File', options.file);

            return formData;
        },
    });

    if (response.error) {
        throw response.error;
    }

    return response.data as ElectricalProjectFileUploadResult;
}

export function useElectricalProjectFileUpload<Endpoint extends SelectableEndpoint>({
    endpoint,
    mutationOptions = {},
}: UseFileUploadOptions<Endpoint>): UseFileUploadResult<Endpoint> {
    const isUserOffline = useOffline();
    const canOfflineFiles = useFeatureFlag({ flag: 'offline-files' });

    const { context: { electricalProjectVersionId } } = useElectricalProject();
    const { generateMediaFileId, files } = useElectricalProjectFiles();
    const mediaManager = useMediaManager();
    const queryClient = useQueryClient();

    const projectFilesQueryOptions = elektraAppQueryOptions.project.files.listOptions({ electricalProjectVersionId });

    const query = api.useQuery('get', endpoint as any, {});

    const mutation = useMutation<ElectricalProjectFileUploadResult, Error, FileUploadMutationOptions<Endpoint>, Promise<FileUploadMutationContext>>({
        ...mutationOptions,
        mutationKey: ['post', endpoint],
        mutationFn: (variables) => {
            return handleMutation(endpoint, variables);
        },
        onMutate: async (variables) => {
            await queryClient.cancelQueries(projectFilesQueryOptions);

            const newFile: ElectricalProjectFile = {
                ElectricalProjectVersionId: electricalProjectVersionId,
                ElectricalProjectFileId: variables.query.ElectricalProjectFileId,
                Description: variables.query.Description,
                Order: variables.query.Order,
                HasFile: false,
                File: {
                    FileId: null,
                    Name: variables.file.name,
                    ContentType: variables.file.type,
                    Size: variables.file.size,
                },
                Kind: SELECTABLE_ENDPOINTS[endpoint] ?? null,
            };

            const localFile = await mediaManager.upload(variables.file, generateMediaFileId(newFile));

            const newFiles = [...files, newFile];

            queryClient.setQueryData(projectFilesQueryOptions.queryKey, {
                Files: newFiles,
                Count: newFiles.length,
            });

            if (mutationOptions.onMutate) {
                await mutationOptions.onMutate(newFile, localFile);
            }

            return { previousFiles: files, mediaFile: localFile };
        },
        onSuccess: async (data, variables, context) => {
            const contextData = await context;

            if (contextData.mediaFile) {
                await mediaManager.remove(contextData.mediaFile);
            }

            queryClient.invalidateQueries(projectFilesQueryOptions);

            if (mutationOptions.onSuccess) {
                await mutationOptions.onSuccess(data, variables, contextData);
            }
        },
        onError: async (error, variables, context) => {
            const contextData = await context;

            if (contextData?.previousFiles) {
                queryClient.setQueryData(projectFilesQueryOptions.queryKey, contextData.previousFiles);
            }

            if (mutationOptions.onError) {
                await mutationOptions.onError(error, variables, contextData!);
            }
            else {
                logger.error({
                    message: 'useElectricalProjectFileUpload',
                    description: 'ElectricalProjectFileId not found',
                    variables,

                }, {
                    apiUrl: endpoint,
                    apiHttpMethod: 'Post',
                });
            }

            console.error(error);
        },
    });

    const accept = useMemo(() => {
        if (!query.data || !query.data.AllowedFileTypes)
            return null;

        return makeAcceptString({
            subtype: query.data.AllowedFileTypes,
        });
    }, [query.data]);

    const upload = useCallback((options: {
        file: FileUploadMutationOptions<Endpoint>['file'];
        query?: Omit<FileUploadMutationOptions<Endpoint>['query'], 'ElectricalProjectVersionId'>;
    }) => {
        return mutation.mutateAsync({
            file: options.file,
            query: {
                ...options.query ?? {},
                ElectricalProjectVersionId: electricalProjectVersionId,
                ElectricalProjectFileId: options.query?.ElectricalProjectFileId ?? crypto.randomUUID(),
            } as FileUploadMutationOptions<Endpoint>['query'],
        });
    }, [electricalProjectVersionId, mutation]);

    const isDisabled = useMemo(() => {
        return (isUserOffline && !canOfflineFiles) || mutation.isPending || query.isLoading;
    }, [isUserOffline, canOfflineFiles, mutation.isPending, query.isLoading]);

    return {
        mutation,
        query,
        accept,
        upload,
        isUploading: mutation.isPending,
        isLoading: query.isLoading,
        isDisabled,
    };
}
